import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentsApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const res = await paymentsApi.getStatus(sessionId);
          setPaymentData(res.data);

          if (res.data.payment_status === 'paid') {
            setStatus('success');
            toast.success('Payment successful!');
            return;
          } else if (res.data.status === 'expired') {
            setStatus('error');
            toast.error('Payment session expired');
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('pending');
          }
        } catch (err) {
          setStatus('error');
          toast.error('Failed to verify payment');
        }
      };

      poll();
    };

    checkStatus();
  }, [sessionId]);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: paymentData?.currency || 'GBP',
    }).format(cents / 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-2 p-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-navy animate-spin mb-4" />
              <CardTitle>Taking payment…</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-signal-ok mb-4" />
              <CardTitle className="text-signal-ok">Paid</CardTitle>
            </>
          )}
          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-signal-warn mb-4" />
              <CardTitle className="text-signal-warn">Payment Processing</CardTitle>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-signal-stop mb-4" />
              <CardTitle className="text-signal-stop">Payment Failed</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'success' && paymentData && (
            <p className="text-2xl font-bold">{formatCurrency(paymentData.amount_total)}</p>
          )}
          {status === 'pending' && (
            <p className="text-muted-foreground">
              Your payment is being processed. Please check back shortly.
            </p>
          )}
          {status === 'error' && (
            <p className="text-muted-foreground">
              There was an issue with your payment. Please try again.
            </p>
          )}
          <div className="flex gap-3 justify-center pt-4">
            {/* One way back. "Dashboard" went to a route the till doesn't have. */}
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/tables')} data-testid="back-to-tables-btn">
              Back to the floor
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
